import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles, EyeOff, Lock, Send, Pencil, Save } from 'lucide-react'
import { api } from '@/lib/api-client'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge, SeverityBadge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { formatCurrency } from '@roi365/shared'
import { toast } from 'sonner'
import { useState } from 'react'

interface AdminRec {
  id: string
  tenant_id: string
  tenant_name: string
  type: string
  category: string
  title: string
  description: string
  client_title: string | null
  client_description: string | null
  admin_notes: string | null
  severity: string
  priority: number
  confidence: number
  estimated_monthly_saving: number
  origin: string
  status: string
  visibility: string
  user_display_name: string | null
  user_principal_name: string | null
  license_display_name: string | null
  created_at: string
}

const originLabels: Record<string, string> = { rule: 'Regla', ai_enriched: 'IA Enriquecida', ai_generated: 'IA Generada' }
const categoryLabels: Record<string, string> = {
  cost_optimization: 'Optimizacion costes', adoption: 'Adopcion', security: 'Seguridad', compliance: 'Cumplimiento',
}
const visibilityLabels: Record<string, string> = { public: 'Publica', internal_only: 'Solo interna', hidden: 'Oculta' }

export function AdminAiRecommendationsPage() {
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState({ status: '', category: '', origin: '', severity: '', visibility: '', search: '' })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ clientTitle: '', clientDescription: '', adminNotes: '', severity: '', priority: 3, visibility: 'public', estimatedMonthlySaving: 0 })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'ai', 'recommendations'] })

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ai', 'recommendations', filters],
    queryFn: () => {
      const params = new URLSearchParams({ perPage: '100' })
      for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v)
      return api.get<AdminRec[]>(`/admin/ai/recommendations?${params}`)
    },
  })

  const editMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.put(`/admin/ai/recommendations/${id}`, data),
    onSuccess: () => { toast.success('Recomendacion actualizada'); invalidate(); setEditingId(null) },
    onError: (e) => toast.error((e as Error).message),
  })

  const publishMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/ai/recommendations/${id}/publish`),
    onSuccess: () => { toast.success('Publicada'); invalidate() },
  })

  const hideMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/ai/recommendations/${id}/hide`),
    onSuccess: () => { toast.success('Oculta'); invalidate() },
  })

  const internalMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/ai/recommendations/${id}/internal`),
    onSuccess: () => { toast.success('Marcada como interna'); invalidate() },
  })

  const startEdit = (rec: AdminRec) => {
    setEditingId(rec.id)
    setEditForm({
      clientTitle: rec.client_title ?? rec.title,
      clientDescription: rec.client_description ?? rec.description,
      adminNotes: rec.admin_notes ?? '',
      severity: rec.severity,
      priority: rec.priority,
      visibility: rec.visibility,
      estimatedMonthlySaving: rec.estimated_monthly_saving,
    })
  }

  const recs = data ?? []

  return (
    <div className="space-y-6">
      <PageHeader title="Recomendaciones IA (Global)" description="Vista cross-tenant de todas las recomendaciones IA" />

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <input placeholder="Buscar..." value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
            <option value="">Todos estados</option>
            <option value="draft">Borrador</option>
            <option value="pending_review">Pendiente revision</option>
            <option value="published">Publicada</option>
            <option value="accepted">Aceptada (cliente)</option>
            <option value="dismissed">Descartada (cliente)</option>
            <option value="applied">Aplicada (cliente)</option>
            <option value="rejected_admin">Rechazada (admin)</option>
            <option value="resolved">Resuelta</option>
            <option value="expired">Caducada</option>
            <option value="superseded">Sustituida (reglas)</option>
          </select>
          <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
            <option value="">Todas categorias</option>
            {Object.entries(categoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.origin} onChange={(e) => setFilters((f) => ({ ...f, origin: e.target.value }))} className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
            <option value="">Todos origenes</option>
            {Object.entries(originLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.severity} onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))} className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
            <option value="">Todas severidades</option>
            <option value="critical">Critica</option>
            <option value="high">Alta</option>
            <option value="medium">Media</option>
            <option value="low">Baja</option>
          </select>
          <select value={filters.visibility} onChange={(e) => setFilters((f) => ({ ...f, visibility: e.target.value }))} className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
            <option value="">Toda visibilidad</option>
            {Object.entries(visibilityLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </Card>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-32 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />)}</div>
      ) : recs.length === 0 ? (
        <Card><EmptyState icon={Sparkles} title="Sin recomendaciones" description="No hay recomendaciones que coincidan con los filtros" /></Card>
      ) : (
        <div className="space-y-3">
          {recs.map((rec) => {
            const isEditing = editingId === rec.id
            return (
              <Card key={rec.id}>
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <input value={editForm.clientTitle} onChange={(e) => setEditForm((f) => ({ ...f, clientTitle: e.target.value }))} placeholder="Titulo cliente" className="rounded-md border px-3 py-2 text-sm font-semibold" style={{ borderColor: 'var(--color-border)' }} />
                      <div className="flex gap-2">
                        <select value={editForm.severity} onChange={(e) => setEditForm((f) => ({ ...f, severity: e.target.value }))} className="flex-1 rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
                          <option value="low">Baja</option>
                          <option value="medium">Media</option>
                          <option value="high">Alta</option>
                          <option value="critical">Critica</option>
                        </select>
                        <select value={editForm.visibility} onChange={(e) => setEditForm((f) => ({ ...f, visibility: e.target.value }))} className="flex-1 rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
                          <option value="public">Publica</option>
                          <option value="internal_only">Solo interna</option>
                          <option value="hidden">Oculta</option>
                        </select>
                        <input type="number" value={editForm.estimatedMonthlySaving} onChange={(e) => setEditForm((f) => ({ ...f, estimatedMonthlySaving: parseFloat(e.target.value) || 0 }))} placeholder="EUR/mes" className="w-24 rounded-md border px-3 py-2 text-sm tabular-nums" style={{ borderColor: 'var(--color-border)' }} />
                      </div>
                    </div>
                    <textarea value={editForm.clientDescription} onChange={(e) => setEditForm((f) => ({ ...f, clientDescription: e.target.value }))} rows={3} placeholder="Descripcion cliente" className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
                    <textarea value={editForm.adminNotes} onChange={(e) => setEditForm((f) => ({ ...f, adminNotes: e.target.value }))} rows={2} placeholder="Notas internas (admin)" className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
                    <div className="flex gap-2">
                      <Button variant="primary" size="sm" onClick={() => editMutation.mutate({ id: rec.id, data: editForm })} loading={editMutation.isPending}>
                        <Save className="h-3.5 w-3.5" /> Guardar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Cancelar</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="premium">{rec.tenant_name}</Badge>
                        <SeverityBadge severity={rec.severity} />
                        <Badge variant={rec.origin === 'rule' ? 'neutral' : rec.origin === 'ai_enriched' ? 'info' : 'premium'}>{originLabels[rec.origin] ?? rec.origin}</Badge>
                        <Badge variant="neutral">{categoryLabels[rec.category] ?? rec.category}</Badge>
                        <Badge variant={rec.visibility === 'public' ? 'success' : rec.visibility === 'internal_only' ? 'warning' : 'error'}>
                          {visibilityLabels[rec.visibility] ?? rec.visibility}
                        </Badge>
                        {rec.confidence > 0 && (
                          <span className="text-xs font-medium" style={{ color: 'var(--color-text-tertiary)' }}>
                            {Math.round(rec.confidence * 100)}% confianza
                          </span>
                        )}
                      </div>
                      <h4 className="mt-2 font-semibold" style={{ color: 'var(--color-text-primary)' }}>{rec.client_title ?? rec.title}</h4>
                      <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{rec.client_description ?? rec.description}</p>
                      {rec.user_display_name && (
                        <p className="mt-1 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                          Usuario: {rec.user_display_name} ({rec.user_principal_name})
                        </p>
                      )}
                      {rec.admin_notes && (
                        <div className="mt-2 rounded-md px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-gray-50)' }}>
                          <strong>Notas internas:</strong> {rec.admin_notes}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--color-brand-accent-2)' }}>
                        {rec.estimated_monthly_saving > 0 ? `${formatCurrency(rec.estimated_monthly_saving)}/mes` : ''}
                      </p>
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button variant="secondary" size="sm" onClick={() => startEdit(rec)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => publishMutation.mutate(rec.id)} title="Publicar">
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => internalMutation.mutate(rec.id)} title="Marcar interna">
                          <Lock className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => hideMutation.mutate(rec.id)} title="Ocultar">
                          <EyeOff className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
